// The portfolio: every product by stage, what needs handling, recent products
// apps/smart-catalog/frontend/src/services/portfolioService.ts
//
// The server decides what each card, table and row shows (bom-v2
// portfolio.service.ts); these types mirror it and the page only lays it out.

import type { BomGroupBy } from '../types/bomV2';
import apiClient from './api';

export type StageId = 'DESIGN' | 'NPI' | 'PRODUCTION' | 'EOL';
export type StageKey = 'ALL' | StageId | 'UNSET';
export type Pillar = 'overview' | 'sourcing' | 'changes' | 'health';
export type Tone = 'red' | 'amber' | 'ok' | 'green' | 'none';

export interface PortfolioCard {
  key: string;
  label: string;
  value: string;
  unit?: string;
  note?: string;
  tone?: Tone;
  parts?: Array<{ label: string; value: number; color: string }>;
  legend?: boolean;
  bars?: Array<{ label: string; value: number }>;
}
/** The headline row: five cards, the same in every stage. */
export interface HeadlineReason {
  key: string;
  label: string;
  count: number;
  tone: 'red' | 'amber' | 'none';
}
export interface HeadlineCard {
  key: 'schedule' | 'supply' | 'changes' | 'package' | 'cost';
  label: string;
  need?: number;
  of?: number;
  reasons?: HeadlineReason[];
  /** Cost only. `savings` (ready + pending) and `readyShare` (ready of that) are
   *  missing from a server that predates them. */
  money?: { value: string; ready: string; pending: string; readyFrac: number; pendingFrac: number; savings?: string; readyShare?: number };
}
export interface PanelCell {
  v: string;
  tone?: Tone;
  sub?: string;
  bar?: Array<{ value: number; color: string; label: string }>;
}
export interface PanelLink {
  stage?: StageKey;
  step?: string;
  bomId?: string;
  changeId?: string;
  partId?: string;
}
export interface PortfolioPanel {
  title: string;
  /** For "Needs handling": "stuck|going". Otherwise a short note. */
  right?: string;
  columns: Array<{ label: string; align: 'left' | 'right' }>;
  rows: Array<{ key: string; link?: PanelLink; cells: PanelCell[] }>;
  total: number;
  foot?: string;
}
export interface PortfolioSummary {
  stages: {
    total: number;
    hidden: number;
    unset: number;
    stages: Array<{ id: StageId; name: string; steps: string[]; count: number }>;
    plan: Record<string, number>;
  };
  stage: StageKey;
  pillar: Pillar;
  headline: HeadlineCard[];
  cards: PortfolioCard[];
  table: PortfolioPanel;
  list: PortfolioPanel;
}
export interface Ring {
  cap: string;
  detail: string;
  frac: number;
  tone: Tone;
  center: string;
}
export interface Stat {
  value: string;
  cap: string;
  tone?: Tone;
}
export interface ProductRow {
  bomId: string;
  pill: string;
  name: string;
  statusText: string;
  statusTone: Tone;
  meta: string;
  rings: [Ring, Ring];
  stats: [Stat, Stat, Stat, Stat];
  /** The caller's own star (product list only). */
  isFavorite?: boolean;
  /** The product's labels and stage, for the row menu (product list only). */
  labels?: string[];
  stageId?: StageId | null;
}
/** A folder of the product list: a label, an import type or a status. */
export interface ProductGroup {
  key: string;
  name: string;
  count: number;
  /** Parts without a supplier across the folder's products. */
  notSourced: number;
  /** Parts across the folder's products and those with a supplier; late
   *  products; value as the rows show money. Missing from an older server. */
  parts?: number;
  sourced?: number;
  late?: number;
  value?: string;
  rows: ProductRow[];
}
export interface ProductPage {
  total: number;
  offset: number;
  limit: number;
  rows: ProductRow[];
  /** Starred products in this stage, whatever the search. */
  favoritesCount: number;
  /** Set when the list is grouped; then `rows` is empty and nothing is paged. */
  groups?: ProductGroup[];
}

const portfolioService = {
  async summary(stage: StageKey, pillar: Pillar, preview: boolean): Promise<PortfolioSummary> {
    const { data } = await apiClient.get<PortfolioSummary>('/bom-v2/portfolio/summary', { params: { stage, pillar, preview } });
    return data;
  },
  /**
   * `pn` searches the product's name or part number; `q` its details.
   * `favorites` keeps the caller's stars; `groupBy` folds the list into folders.
   */
  async products(p: {
    stage: StageKey;
    pillar: Pillar;
    pn: string;
    q: string;
    offset: number;
    limit: number;
    preview: boolean;
    favorites?: boolean;
    groupBy?: BomGroupBy;
  }): Promise<ProductPage> {
    const { data } = await apiClient.get<ProductPage>('/bom-v2/portfolio/products', { params: p });
    return data;
  },
  async setStage(bomId: string, stage: StageId | null, step?: string) {
    const { data } = await apiClient.patch(`/bom-v2/${bomId}/stage`, { stage, step });
    return data;
  },
};

export default portfolioService;
